import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Col, Container } from "reactstrap";
import { customerCardsListColumns } from "pages/warehouse/customerCards/customerCardsListColumns";
import { convertCard, retrieveAllAssets } from "utils/api";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import NGITable from "components/Common/NGITable";
import { FILTER_PAGE } from "utils/constants";
import useStoreFilters from "customHooks/useStoreFilters";
import { getDictionary } from "store/dictionaries/actions";
import { DICTIONARY_TYPES } from "store/dictionaries/constants";
import { useDispatch } from "react-redux";
import CustomerCardsFilter from "pages/warehouse/customerCards/customerCardsFilter";
import { isEmpty } from "lodash";
import CustomButton from "components/Common/buttons/CustomButton";
import AddRfidCardsModal from "pages/warehouse/customerCards/AddRfidCardsModal";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import CardConversionModal from "pages/warehouse/customerCards/CardConversionModal";
import convertIcon from "assets/images/svg/convert-card.svg";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles({
  convertIcon: {
    "& .image-icon-btn": {
      height: 14,
    },
  },
});

const CustomerCardsPage = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [isLoading, setLoader] = useState(false);
  const { filters, setFilters } = useStoreFilters(FILTER_PAGE.CUSTOMER_CARDS);
  const [list, setList] = useState([]);
  const [openAddRfidModal, setOpenAddRfidModal] = useState(false);
  const [openConvertModal, setOpenConvertModal] = useState(false);
  const [activeItem, setActiveItem] = useState(null);

  useEffect(() => {
    handleFilter(!isEmpty(filters) ? filters : { group: "Card" });

    dispatch(getDictionary(DICTIONARY_TYPES.DEPOT));
  }, []);

  const handleFilter = useCallback((options = {}) => {
    setFilters(options);
    setLoader(true);

    retrieveAllAssets({ ...options })
      .then(res => {
        setList(res?.data || null);
        setLoader(false);
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, []);

  const onOpenAddRfidModal = useCallback(() => setOpenAddRfidModal(true), []);
  const onCloseAddRfidModal = useCallback(() => setOpenAddRfidModal(false), []);

  const onOpenConvertModal = useCallback(() => setOpenConvertModal(true), []);
  const onCloseConvertModal = useCallback(() => setOpenConvertModal(false), []);

  const onConvert = useCallback(cardInfo => setActiveItem(cardInfo), []);

  const closeConfirmationModal = useCallback(() => setActiveItem(null), []);

  const onConfirm = useCallback(() => {
    setLoader(true);
    const newType = activeItem.type === "RFID Card" ? "Virtual Card" : "RFID Card";

    convertCard(activeItem.id, newType)
      .then(() => {
        setLoader(false);
        successMessage(`Card ${activeItem.cardAttributes.number} was successfully converted to ${newType}`);
        handleFilter(!isEmpty(filters) ? filters : { group: "Card" });
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, [activeItem]);

  const columns = useMemo(() => customerCardsListColumns(onConvert), []);

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <div className="filters-wrapper">
            <div className="filter-buttons">
              <CustomButton
                icon={<i className="bx bx-plus-circle" />}
                type="primary"
                onClick={onOpenAddRfidModal}
                text="CREATE CARDS"
              />

              <CustomButton
                className={classes.convertIcon}
                iconImg={convertIcon}
                type="primary"
                onClick={onOpenConvertModal}
                text="CONVERT CARDS"
              />
            </div>

            <CustomerCardsFilter onFilter={handleFilter} isLoading={isLoading} />
          </div>
          <Col>
            <NGITable
              filtersType={FILTER_PAGE.CUSTOMER_CARDS}
              columns={columns}
              data={list}
              isLoading={isLoading}
              updateList={handleFilter}
            />
          </Col>
          {openAddRfidModal && <AddRfidCardsModal onCancel={onCloseAddRfidModal} />}
          {openConvertModal && <CardConversionModal onCancel={onCloseConvertModal} />}
          {!!activeItem && (
            <ConfirmationModal
              description={
                <span>
                  Are you sure you want to convert this card <b>{activeItem.cardAttributes.number}</b> to{" "}
                  {activeItem.type === "RFID Card" ? "Virtual Card" : "RFID Card"}?
                </span>
              }
              confirmBtnTitle="CONVERT"
              cancelBtnTitle="CANCEL"
              onCancel={closeConfirmationModal}
              onConfirm={onConfirm}
            />
          )}
        </Container>
      </div>
    </>
  );
};

export default CustomerCardsPage;
